import { HttpClient } from "@angular/common/http";
import { computed, Injectable, signal } from "@angular/core";
import { HttpResponsesHandlersFactory } from "@bbwt/modules/data-service/http-responses-handler";
import { BaseDataService } from "../../bbwt/modules/data-service";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";

export enum SrxUserStatus {
    Enabled = 0,
    NotMapped = 1,
    MissingCredentials = 2,
    DisabledForOrganization = 3
}

@Injectable({
    providedIn: "root"
})
export class SrxService extends BaseDataService {
    readonly url = "api/srx";

    constructor(http: HttpClient, handlersFactory: HttpResponsesHandlersFactory, private patientService: PatientService) {
        super(http, handlersFactory);
    }

    protected readonly _srxUserStatus = signal<SrxUserStatus>(SrxUserStatus.DisabledForOrganization);

    readonly srxUserStatus = this._srxUserStatus.asReadonly();
    readonly srxAvailableForOrg = computed(() => this.srxUserStatus() === SrxUserStatus.Enabled || this.srxUserStatus() === SrxUserStatus.NotMapped)

    async reloadSrxUserStatus() {
        this._srxUserStatus.set(await this.getSrxStatus());
    }

    testSrxCredentials(email: string, password: string, organizationId: string): Promise<boolean> {
        return this.handleRequest(
            this.http.post<boolean>(`${this.url}/test-credentials`, {
                email: email,
                password: password,
                organizationId: organizationId
            }),
            this.handlersFactory.getForReadAll()
        );
    }

    saveSrxCredentials(email: string, password: string, organizationId: string): Promise<boolean> {
        return this.handleRequest(
            this.http.post<boolean>(`${this.url}/save-credentials`, {
                email: email,
                password: password,
                organizationId: organizationId
            }),
            this.handlersFactory.getForReadAll()
        );
    }

    testOAuth2Credentials(clientId: string, clientSecret: string, organizationId: string): Promise<boolean> {
        return this.handleRequest(
            this.http.post<boolean>(`${this.url}/oauth2/test-credentials`, {
                clientId: clientId,
                clientSecret: clientSecret,
                organizationId: organizationId
            }),
            this.handlersFactory.getForReadAll()
        );
    }

    saveOAuth2Credentials(clientId: string, clientSecret: string, organizationId: string): Promise<boolean> {
        return this.handleRequest(
            this.http.post<boolean>(`${this.url}/oauth2/save-credentials`, {
                clientId: clientId,
                clientSecret: clientSecret,
                organizationId: organizationId
            }),
            this.handlersFactory.getForReadAll()
        );
    }

    saveWebhookSecret(webhookSecret: string, organizationId: string) {
        return this.handleRequest(
            this.http.post<boolean>(`${this.url}/webhook-secret`, {
                webhookSecret: webhookSecret,
                organizationId: organizationId
            }),
            this.handlersFactory.getForReadAll()
        );
    }

    getSrxStatus(): Promise<SrxUserStatus> {
        return this.handleRequest(
            this.http.get<SrxUserStatus>(`${this.url}/status`),
            this.handlersFactory.getDefault()
        );
    }

    getIntegrationCode(): Promise<string> {
        return this.handleRequest(
            this.http.get(`${this.url}/integration-code`, { responseType: "text" }),
            this.handlersFactory.getDefault()
        );
    }

    getAppUrl(): Promise<string> {
        return this.handleRequest<string>(
            this.http.get(`${this.url}/app-url`, { responseType: "text" }),
            this.handlersFactory.getDefault()
        );
    }

    getWebhookUrl(organizationId: string, organizationName: string): Promise<string> {
        return this.handleRequest(
            this.http.get(`${this.url}/webhook-url/${organizationId}/${organizationName}`, { responseType: "text" }),
            this.handlersFactory.getDefault()
        );
    }
}